import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

interface AiSettingsContextValue {
  accountId: string | null;
  aiUrl: string | null;
  isAiPanelOpen: boolean;
  setIsAiPanelOpen: (open: boolean) => void;
}

const AiSettingsContext = createContext<AiSettingsContextValue | null>(null);

export function useAiSettings() {
  const context = useContext(AiSettingsContext);
  if (!context) {
    throw new Error('useAiSettings must be used within an AiSettingsProvider.');
  }
  return context;
}

interface AiSettingsProviderProps {
  accountId: string | null;
  aiUrl: string | null;
  children: ReactNode;
  isAiPanelOpen?: boolean;
  onIsAiPanelOpenChange?: (open: boolean) => void;
}

const noop = () => {};

export function AiSettingsProvider({
  accountId,
  aiUrl,
  children,
  isAiPanelOpen = false,
  onIsAiPanelOpenChange = noop,
}: AiSettingsProviderProps) {
  const value = useMemo(
    () => ({ accountId, aiUrl, isAiPanelOpen, setIsAiPanelOpen: onIsAiPanelOpenChange }),
    [accountId, aiUrl, isAiPanelOpen, onIsAiPanelOpenChange],
  );

  return <AiSettingsContext.Provider value={value}>{children}</AiSettingsContext.Provider>;
}
